import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { I18nProvider } from 'next-localization';

import { FeaturesContext } from '@gn-audio/jss-components/components';
import BasketProvider from '@gn-audio/jss-components/components/Basket/BasketContext/BasketProvider';
import { SiteSettingsContext } from '@gn-audio/jss-components/utils/contexts';
import { TrackingProvider } from '@gn-audio/jss-components/utils/providers';
import {
  configureAuthorization,
  getLocaleWithRegion,
  initializeBasketHttp,
} from '@gn-audio/jss-core';

import { FormatSettings, getCurrencyFormats } from 'api/pricesApi/formattingApi';
import { AppSettingContext } from 'common/appSettings/AppSettingContext';
import { FormatContext } from 'common/contexts/FormatContext';
import {
  configureMavenoidApi,
  configurePricingApi,
  initializeHttpClients,
  SITE_CONTEXT,
} from 'common/core-components/bootstrapApis';
import { JabraThemesListContext } from 'common/jabraTheme/JabraThemesListContext';
import Bootstrap from 'src/Bootstrap';
import { isNotNil } from 'utils/utils';

import ErrorPage from './_error';
import { JabraPageProps } from './[[...path]]';

import 'assets/app.css';
import './../../styles/globals.css';

function App({ Component, pageProps }: AppProps<JabraPageProps>): JSX.Element {
  const router = useRouter();
  // todo: disables next prefetch mechanism. remove after ending coexistence period
  useMemo(() => {
    router.prefetch = async () => {};
  }, [router]);

  const {
    envConfig,
    jabraDictionary,
    locale,
    error: serverError,
    themes,
    appSettings,
    siteSettings,
    features,
    ...rest
  } = pageProps;

  const language = getLocaleWithRegion(locale);
  const [formatSettings, setFormatSettings] = useState<FormatSettings | undefined>(undefined);

  const bootstrapApp = () => {
    initializeHttpClients(envConfig);

    if (siteSettings?.sfcc) {
      configurePricingApi(siteSettings.sfcc, features.SfccPrice ?? false, language);
    }

    if (siteSettings?.authorization) {
      configureAuthorization(siteSettings, SITE_CONTEXT as any, language);
      configureMavenoidApi(siteSettings.authorization);
    }
  };

  bootstrapApp();

  const initializeSfccServices = useCallback(async () => {
    if (typeof window !== 'undefined' && envConfig && siteSettings) {
      try {
        if (siteSettings.sfcc) await initializeBasketHttp(siteSettings.sfcc);

        const formatSettings = await getCurrencyFormats(siteSettings, language);
        setFormatSettings(formatSettings);
      } catch (error) {
        console.error('Error bootstrapping client-side components:', error);
      }
    }
  }, [envConfig, siteSettings, language]);

  useEffect(() => {
    initializeSfccServices();
  }, [initializeSfccServices]);

  if (serverError) {
    console.error(serverError);
    return <ErrorPage />;
  }

  const validThemes = (themes ?? []).filter(isNotNil);

  return (
    <>
      <Bootstrap {...pageProps} />
      {/*
        // Use the next-localization (w/ rosetta) library to provide our translation dictionary to the app.
        // Note Next.js does not (currently) provide anything for translation, only i18n routing.
        // If your app is not multilingual, next-localization and references to it can be removed.
      */}
      <TrackingProvider>
        <FormatContext.Provider value={formatSettings}>
          <SiteSettingsContext.Provider value={siteSettings ?? {}}>
            <AppSettingContext.Provider value={appSettings}>
              <JabraThemesListContext.Provider value={validThemes}>
                <FeaturesContext.Provider value={features}>
                  <I18nProvider lngDict={jabraDictionary} locale={pageProps.locale}>
                    <BasketProvider>
                      <Component {...rest} locale={locale} />
                    </BasketProvider>
                  </I18nProvider>
                </FeaturesContext.Provider>
              </JabraThemesListContext.Provider>
            </AppSettingContext.Provider>
          </SiteSettingsContext.Provider>
        </FormatContext.Provider>
      </TrackingProvider>
    </>
  );
}

export default App;
